import { jsPDF } from "jspdf";
import JSZip from "jszip";

const formatCurrency = (amount, currency) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency?.toUpperCase?.() || "USD" }).format(amount || 0);

const buildCsvString = ({ account, purchases }) => {
  const headers = ["plan", "email", "amount", "currency", "payment_status", "created_at", "receipt_reference", "access_expires_at"];
  const rows = purchases.map((purchase) => [
    purchase.plan_name,
    account.email,
    purchase.amount,
    purchase.currency,
    purchase.payment_status,
    purchase.created_at,
    purchase.receipt_reference,
    purchase.access_expires_at || "",
  ]);
  return [headers, ...rows]
    .map((row) => row.map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`).join(","))
    .join("\n");
};

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const createLedgerSignature = async ({ account, purchase }) => {
  const payload = `${account.email}|${purchase.receipt_reference}|${purchase.amount}|${purchase.created_at}|${purchase.payment_status}`;
  const buffer = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(payload));
  return Array.from(new Uint8Array(buffer))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 28)
    .toUpperCase();
};

const buildInvoiceDoc = async ({ account, purchase }) => {
  const signature = await createLedgerSignature({ account, purchase });
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFillColor(8, 8, 14);
  doc.rect(0, 0, 595, 842, "F");

  doc.setTextColor(228, 216, 185);
  doc.setFontSize(11);
  doc.text("ANATOMIA OCCULTI · KŌNSHUS PREMIUM", 48, 52);
  doc.setFontSize(28);
  doc.text("Signed Invoice", 48, 96);
  doc.setDrawColor(212, 175, 55);
  doc.line(48, 116, 547, 116);

  doc.setFontSize(12);
  doc.setTextColor(230, 230, 235);
  doc.text(`Member: ${account.email}`, 48, 156);
  doc.text(`Plan: ${purchase.plan_name}`, 48, 178);
  doc.text(`Receipt reference: ${purchase.receipt_reference}`, 48, 200);
  doc.text(`Status: ${purchase.payment_status}`, 48, 222);
  doc.text(`Purchase date: ${purchase.created_at}`, 48, 244);
  doc.text(`Access expiry: ${purchase.access_expires_at || "—"}`, 48, 266);

  doc.setFillColor(16, 16, 24);
  doc.roundedRect(48, 320, 499, 148, 18, 18, "F");
  doc.setTextColor(212, 175, 55);
  doc.setFontSize(12);
  doc.text("Billing summary", 68, 352);
  doc.setTextColor(245, 245, 247);
  doc.setFontSize(30);
  doc.text(formatCurrency(purchase.amount, purchase.currency), 68, 402);
  doc.setFontSize(12);
  doc.text(`Currency: ${purchase.currency?.toUpperCase?.() || "USD"}`, 68, 430);
  doc.text("Signed by the Kōnshus ledger seal for the premium archive.", 68, 452);

  doc.setTextColor(160, 160, 168);
  doc.setFontSize(10);
  doc.text(`Ledger signature: ${signature}`, 48, 772);
  doc.text("Kōnshus member billing ledger · Premium ceremonial account record", 48, 792);
  return doc;
};

export const downloadBillingCsv = ({ account, purchases }) => {
  if (!account || !Array.isArray(purchases) || purchases.length === 0) return;
  const blob = new Blob([buildCsvString({ account, purchases })], { type: "text/csv;charset=utf-8;" });
  saveBlob(blob, `anatomia-occulti-billing-${account.email}.csv`);
};

export const downloadInvoicePdf = async ({ account, purchase }) => {
  if (!account || !purchase) return;
  const doc = await buildInvoiceDoc({ account, purchase });
  doc.save(`anatomia-occulti-invoice-${purchase.receipt_reference}.pdf`);
};

export const printInvoiceView = ({ account, purchase }) => {
  if (!account || !purchase) return;
  const windowRef = window.open("", "_blank", "width=960,height=900");
  if (!windowRef) return;

  windowRef.document.write(`
    <html>
      <head>
        <title>Invoice ${purchase.receipt_reference}</title>
        <style>
          body { font-family: Georgia, serif; background: #09090f; color: #f2efe7; padding: 40px; }
          .card { max-width: 840px; margin: 0 auto; border: 1px solid rgba(212,175,55,.28); border-radius: 24px; padding: 32px; background: #12121a; }
          .label { letter-spacing: .28em; text-transform: uppercase; font-size: 12px; color: #d4af37; }
          h1 { font-size: 46px; margin: 12px 0 24px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
          .item { padding: 16px; border-radius: 18px; background: #0c0c13; }
        </style>
      </head>
      <body>
        <div class="card">
          <div class="label">Anatomia Occulti · Kōnshus Premium</div>
          <h1>Invoice</h1>
          <div class="grid">
            <div class="item"><strong>Member</strong><br/>${account.email}</div>
            <div class="item"><strong>Plan</strong><br/>${purchase.plan_name}</div>
            <div class="item"><strong>Receipt Reference</strong><br/>${purchase.receipt_reference}</div>
            <div class="item"><strong>Payment Status</strong><br/>${purchase.payment_status}</div>
            <div class="item"><strong>Purchase Date</strong><br/>${purchase.created_at}</div>
            <div class="item"><strong>Access Expiry</strong><br/>${purchase.access_expires_at || "—"}</div>
          </div>
          <div class="item" style="margin-top:16px;"><strong>Amount</strong><br/>${formatCurrency(purchase.amount, purchase.currency)}</div>
        </div>
      </body>
    </html>
  `);
  windowRef.document.close();
  windowRef.focus();
  windowRef.print();
};

export const downloadSignedBillingBundle = async ({ account, purchases, analytics }) => {
  if (!account || !Array.isArray(purchases) || purchases.length === 0) return;
  const zip = new JSZip();
  zip.file("billing.csv", buildCsvString({ account, purchases }));

  for (const purchase of purchases) {
    const doc = await buildInvoiceDoc({ account, purchase });
    zip.file(`invoices/${purchase.receipt_reference}.pdf`, doc.output("arraybuffer"));
  }

  zip.file("analytics/summary.json", JSON.stringify(analytics, null, 2));
  zip.file(
    "analytics/summary.txt",
    [
      `Member: ${account.email}`,
      `Total billed: ${analytics.totalBilled}`,
      `Settled purchases: ${analytics.settledPurchases}`,
      `Pending purchases: ${analytics.pendingPurchases}`,
      `Renewal risk: ${analytics.renewalRisk}`,
      `Saved chats: ${analytics.savedChats}`,
    ].join("\n"),
  );

  const blob = await zip.generateAsync({ type: "blob" });
  saveBlob(blob, `anatomia-occulti-ledger-archive-${account.email}.zip`);
};